/**
 * selection-controller.js — the single write path into the selection store.
 *
 * `SelectionStore` is deliberately policy-free. Everything that decides whether
 * a click *becomes* a selection lives here:
 *
 *   - pick mode consumes a click entirely for the STORE (tag the section as
 *     agent context, never select it) — shell.js's own handler `return`s before
 *     tracking in exactly the same case, and still owns the tag emission
 *   - page scoping, pruning on content churn, hard reset on identity boundaries
 *
 * This split is the fix for the failure mode the audit caught: if the bridge or
 * a legacy event handler wrote the store directly, pick mode would be bypassed
 * and an emit→write→emit loop would be one careless subscriber away.
 *
 * ── Authoritative as of Phase 16A0.5 ───────────────────────────────────────
 * The scalar `selectedInstanceId` is gone; this store is the only section
 * selection state. Design notes that matter for parity:
 *
 * 1. The legacy events (`iframe:section-clicked`, `section-deselected`,
 *    `iframe:canvas-clicked`, `section:select`) remain RAW INPUTS, emitted by
 *    their original sites with their original guards. They are deliberately NOT
 *    re-derived from the store in this phase — re-deriving would change
 *    delivery semantics for ~29 listeners (no event on same-section re-click,
 *    which would strand tour advance triggers; no events during pick mode,
 *    which would change panel behaviour). The request-pipeline formalization
 *    happens in 16A1, where each emit site changes anyway.
 *
 * 2. One legacy emission DOES live here: `section-deselected` on a canvas
 *    click. The shell used to guard that emit on the scalar it was about to
 *    clear; this controller runs first (registered before wireEvents), so by
 *    the time the shell's guard read the store it would always be empty and
 *    the event would never fire. The echo therefore moved to the only place
 *    that still knows whether anything WAS selected.
 *
 * 3. `selection:changed` is emitted on the bus for every store change. It is
 *    the forward-looking event 16A1 builds on; today collab presence is its
 *    first consumer (an improvement over `section:select`, which plain canvas
 *    clicks never travelled through — peer badges used to go stale on them).
 *
 * The sticky element-click fallback the scalar carried was reproduced through
 * 16A0.5 for parity, then removed in 16A0.6 — see `_onElementClicked` for the
 * fix and the audit reference. That is the one deliberate behaviour change in
 * the 16A0 migration, disclosed in the phase summary.
 */

import { eventBus } from '../core/events.js';
import { store } from '../core/state.js';
import { selection } from '../core/selection-store.js';
import { pageSectionOrder, sectionOrderForPage } from '../shared/section-order.js';
import { isPickModeActive } from '../editing/ai-context-picker.js';
import { isPaintModeActive, exitPaintMode } from '../editing/format-painter.js';
import { showToast } from './toast.js';

let _installed = false;
let _lastActivePage = null;
/**
 * The section list's view of "visible candidate order" — its rows minus
 * filtered and shared ones, in DOM order. Registered by section-list.js so
 * range gestures and Cmd+A operate on what the user can SEE (a range across a
 * filter must not select invisible sections; that plus delete is data loss).
 * Falls back to the state-derived page order when no provider is registered or
 * the provider abstains (returns null — e.g. body not mounted yet).
 * @type {null | (() => string[] | null)}
 */
let _visibleOrderProvider = null;
/** The shared-row multi-select explainer fires once per session, not per click. */
let _sharedCollapseExplained = false;

/** Ordered candidate ids for a range: active-page sections, shared excluded. */
export function candidateOrder() {
  return pageSectionOrder(store.getState());
}

/**
 * section-list.js registers its visible-row order here (16A1). One provider —
 * the section list is the only surface that filters — and it returns null to
 * abstain rather than an empty array, so "no rows mounted" never reads as
 * "nothing selectable".
 * @param {null | (() => string[] | null)} fn
 */
export function setVisibleOrderProvider(fn) {
  _visibleOrderProvider = typeof fn === 'function' ? fn : null;
}

/** Visible candidate order when a caller supplied none with the request. */
function resolveOrder(order) {
  if (Array.isArray(order) && order.length) return order;
  const provided = _visibleOrderProvider ? _visibleOrderProvider() : null;
  return Array.isArray(provided) && provided.length ? provided : candidateOrder();
}

/** Shared nav/footer ids — scalar-only: never members of a multi-selection. */
function sharedIdSet() {
  const shared = store.getState()?.sharedSections;
  const out = new Set();
  if (shared?.navigation?.instanceId) out.add(shared.navigation.instanceId);
  if (shared?.footer?.instanceId) out.add(shared.footer.instanceId);
  return out;
}

/** Every selectable id, including shared nav/footer — what `prune` validates against. */
function validIds() {
  return sectionOrderForPage(store.getState());
}

/**
 * Identity-boundary reset — project restore, tab-guard promotion, start-over.
 * Exported so shell.js clears selection without writing the store itself;
 * every mutation stays inside this module. Emits no legacy event, matching the
 * old scalar sites, which nulled silently (their flows rebuild the UI wholesale
 * via a full render, so nothing needs a deselect notification).
 */
export function resetSelection(reason) {
  selection.reset({ source: 'shell', reason });
}

// ── policy ──────────────────────────────────────────────────────────────────
// Each handler mirrors the shell handler of the same name, in the same order,
// so a reader can diff them side by side.

function _onSectionClicked(data) {
  // Pick mode CONSUMES the click for the store: the section is tagged as agent
  // context (shell.js emits the tag) and never becomes the keyboard target.
  if (isPickModeActive() && data?.instanceId) return;
  const id = data?.instanceId || null;
  if (id) selection.select(id, { source: 'section-clicked', meta: pickMeta(data) });
  else selection.clear({ source: 'section-clicked' });
}

function _onSectionDeselected() {
  selection.clear({ source: 'section-deselected', reason: 'parent-deselected' });
}

function _onElementClicked(data) {
  if (isPickModeActive() && data?.instanceId && data?.eid) return;
  // Desync fix (16A0.6). The retired scalar did
  //   selectedInstanceId = data?.instanceId || selectedInstanceId
  // leaving the PREVIOUS section as the hidden keyboard target whenever an
  // element click carried no instanceId — reproduced end-to-end in
  // builder/docs/editing-ux-audit-2026-07-21.md (select A, click an editable
  // in B, Delete removes A). The invariant is now: the selected element's
  // section IS the primary selection, and an unusable payload clears rather
  // than silently pointing destructive shortcuts at something else.
  const id = data?.instanceId || null;
  if (id) selection.select(id, { source: 'element-clicked' });
  else selection.clear({ source: 'element-clicked', reason: 'element-without-section' });
}

function _onCanvasClicked() {
  // Echoes `section-deselected` from here — see header note 2. The guard
  // (only emit when something WAS selected) reproduces the shell's old
  // `if (selectedInstanceId)` exactly; clearing an empty selection stays
  // silent, as it always was.
  const had = !selection.isEmpty();
  selection.clear({ source: 'canvas-clicked' });
  if (had) eventBus.emit('section-deselected');
}

/**
 * `section:select` — the deferred post-render reveal emitted by the shell's
 * `selectAfterRender` after undo/redo, a template load, an import, or an
 * add-section. That path never travels through a click event, so without this
 * input the store would stay empty through every one of those flows.
 *
 * The event survives 16A0.5 deliberately (the plan wanted it retired):
 * section-list's handler re-emits `iframe:section-clicked` behind its own
 * dedupe guard, and tour advance triggers key off the re-emit's shape.
 * Retiring it belongs to 16A1's section-list rework, not to the parity step.
 * Collab presence no longer listens to it — that moved to `selection:changed`.
 *
 * Always a single target: it means "show the user what just happened", so it
 * selects rather than extends, even once ranges exist.
 */
function _onProgrammaticSelect(data) {
  const id = data?.instanceId || null;
  if (!id) return;
  selection.select(id, { source: 'section:select' });
}

/**
 * `selection:request` — the multi-select intent pipeline (16A1). Raw gestures
 * (modifier clicks, checkbox toggles, Shift+Arrow, Cmd+A, background click)
 * resolve to an intent in section-list-interaction.js and arrive here; this is
 * where domain policy is applied before the store is written:
 *
 *   - pick mode consumes gestures entirely, same as a plain click
 *   - shared nav/footer are SCALAR-ONLY: a toggle/range on a shared row
 *     collapses to that row alone, with a once-per-session explainer toast
 *   - ranges resolve against the VISIBLE candidate order (filtered rows are
 *     skipped; shared rows are never in a range; ranges never cross pages)
 *   - 'select-all' = visible page sections, shared excluded
 *
 * Plain single clicks do NOT travel through here — they keep the legacy
 * `iframe:section-clicked` path byte-for-byte (single-select parity is 16A0's
 * exit gate), which also means pick-mode tagging and the panel auto-switch
 * keep their exact timing.
 *
 * @param {{intent: string, id?: string, order?: string[], source?: string}} req
 */
function _onSelectionRequest(req) {
  if (!req || !req.intent) return;
  // Format-painter parity with the legacy click path (shell's
  // `iframe:section-clicked` handler exits paint mode before doing anything
  // else). The painter is armed against ONE source section, so any selection
  // change disarms it — and modifier clicks, checkbox ticks, Cmd+A and
  // Shift+Arrow reach the store through HERE, never through that handler.
  // Same order as the shell: paint exits, then pick consumes.
  if (isPaintModeActive()) exitPaintMode();
  if (isPickModeActive()) return;
  const intent = req.intent;
  const source = req.source || 'request';
  const order = resolveOrder(req.order);
  const shared = sharedIdSet();

  // ONE empty-transition echo for every intent. Legacy listeners (property
  // panel, glass overlay, iframe chrome) do their empty-selection cleanup on
  // `section-deselected` and nothing else; `toggle` reaches empty through
  // SelectionStore.clear() *internally*, which is invisible from here without
  // this check — that is how un-ticking the last checkbox used to leave the
  // panel editing a section the store no longer had.
  const hadSelection = !selection.isEmpty();
  const echoIfEmptied = () => {
    if (hadSelection && selection.isEmpty()) eventBus.emit('section-deselected');
  };

  if (intent === 'clear') {
    selection.clear({ source: 'request-clear' });
    echoIfEmptied();
    return;
  }

  if (intent === 'select-all') {
    const ids = order.filter((id) => !shared.has(id));
    if (!ids.length) return;
    selection.set(ids, {
      order,
      anchor: ids[0],
      lead: ids[ids.length - 1],
      source: 'request-select-all'
    });
    return;
  }

  // 'set' — a programmatic wholesale replacement (bulk duplicate selects its
  // clones as ONE write, never N events). Same policy as every other write:
  // shared ids are scalar-only and are dropped from the set here.
  if (intent === 'set') {
    const ids = (Array.isArray(req.ids) ? req.ids : []).filter((x) => x && !shared.has(x));
    if (!ids.length) return;
    selection.set(ids, {
      order,
      anchor: ids[0],
      lead: ids[ids.length - 1],
      source: `${source}:set`
    });
    return;
  }

  const id = req.id || null;
  if (!id) return;

  // A modifier gesture on a shared row collapses to that row alone — shared
  // sections can be the sole selection, never a member of a set of two or
  // more. Explain once, with the existing shared-section phrasing.
  if (shared.has(id) && intent !== 'replace') {
    const wasMulti = selection.isMulti() || !selection.isSelected(id);
    selection.select(id, { source: 'request-shared-collapse' });
    if (!_sharedCollapseExplained && wasMulti) {
      _sharedCollapseExplained = true;
      try {
        showToast("Shared sections can't be part of a multi-selection", { type: 'info' });
      } catch {
        /* headless test environments have no toast container */
      }
    }
    return;
  }

  switch (intent) {
    case 'replace':
      selection.select(id, { source: `${source}:replace` });
      return;
    case 'toggle':
      selection.toggle(id, { order, source: `${source}:toggle` });
      echoIfEmptied();
      return;
    case 'range':
    case 'range-add': {
      // Shared rows are never range members; the anchor lives in page space.
      const rangeOrder = order.filter((x) => !shared.has(x));
      selection.extendTo(id, {
        order: rangeOrder,
        additive: intent === 'range-add',
        source: `${source}:${intent}`
      });
      return;
    }
    default:
      return;
  }
}

/** Only the metadata keys a forwarded payload may carry. */
function pickMeta(data) {
  if (!data) return undefined;
  const meta = {};
  if ('fromIframe' in data) meta.fromIframe = data.fromIframe;
  return Object.keys(meta).length ? meta : undefined;
}

/**
 * Content churn — a collab peer's delete, an undo, a template load. Drops ids
 * that no longer resolve and repairs anchor/lead.
 *
 * Also watches for an active-page change, which is an IDENTITY boundary rather
 * than churn: `prune` alone cannot notice that a selection stayed technically
 * valid across a page switch, nor that a replaced project reused an id.
 */
function _onStateChanged() {
  const state = store.getState();
  const activePage = state?.activePage ?? null;
  if (_lastActivePage !== null && activePage !== _lastActivePage) {
    _lastActivePage = activePage;
    selection.reset({ source: 'state-changed', reason: 'active-page-changed' });
    return;
  }
  _lastActivePage = activePage;
  selection.prune(validIds(), { source: 'state-changed' });
}

/**
 * Install the controller — the sole writer of the selection store.
 * @returns {() => void} uninstall
 */
export function initSelectionController() {
  if (_installed) return () => {};
  _installed = true;
  _lastActivePage = store.getState()?.activePage ?? null;

  // Every store change surfaces on the bus as `selection:changed` — the one
  // derived selection event. Payload is the store's change object verbatim
  // (ids, primary, anchor, lead, previousIds, previousPrimary, size, source).
  const offStore = selection.subscribe((change) => {
    eventBus.emit('selection:changed', change);
  });

  const offs = [
    eventBus.on('iframe:section-clicked', _onSectionClicked),
    eventBus.on('section-deselected', _onSectionDeselected),
    eventBus.on('iframe:element-clicked', _onElementClicked),
    eventBus.on('iframe:canvas-clicked', _onCanvasClicked),
    eventBus.on('section:select', _onProgrammaticSelect),
    eventBus.on('selection:request', _onSelectionRequest),
    eventBus.on('state-changed', _onStateChanged)
  ];

  return () => {
    offStore();
    for (const off of offs) if (typeof off === 'function') off();
    _installed = false;
  };
}

/** Test hook — tears the singleton down so a suite can install a fresh one. */
export function _resetSelectionControllerForTests() {
  _installed = false;
  _lastActivePage = null;
  _visibleOrderProvider = null;
  _sharedCollapseExplained = false;
  selection.clear({ source: 'test-reset' });
}
